import React from 'react';
import { useIntl } from 'react-intl';
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/withStyles';
import { formatMoney, formatDay, photoUrl, DASH } from '../shared/reportingFormat';
import messages from '../../../locale/jsonMessages';
import sh from '../shared/OwnerReporting.css';
import s from './OwnerEarnings.css';

export const STATUS_LABELS = { upcoming: 'orpStatusUpcoming', pendingPayout: 'orpStatusPending', paid: 'orpStatusPaid' };
const STATUS_CLASSES = { upcoming: 'statusUpcoming', pendingPayout: 'statusPending', paid: 'statusPaid' };
const TYPE_PILLS = [
  { key: 'rental', label: 'orpTypeRental', className: 'pillRental' },
  { key: 'options', label: 'orpTypeOptions', className: 'pillOptions' },
  { key: 'delivery', label: 'orpTypeDelivery', className: 'pillDelivery' },
  { key: 'other', label: 'orpTypeOther', className: 'pillOther' },
];

// Component pills shown only for the components the server reports as non-zero.
export const TypePills = ({ components, styles }) => {
  const { formatMessage } = useIntl();
  const present = TYPE_PILLS.filter((pill) => {
    const value = components ? components[pill.key] : null;
    return value !== undefined && value !== null && Number(value) !== 0;
  });
  if (present.length === 0) return <span>{DASH}</span>;
  return (
    <span className={styles.pills}>
      {present.map((pill) => <span key={pill.key} className={cx(styles.pill, styles[pill.className])}>{formatMessage(messages[pill.label])}</span>)}
    </span>
  );
};

export const StatusPill = ({ row, styles }) => {
  const { formatMessage } = useIntl();
  const key = STATUS_LABELS[row.revenueStatus];
  return (
    <span>
      {key ? <span className={cx(styles.pill, styles.statusPill, styles[STATUS_CLASSES[row.revenueStatus]])}>{formatMessage(messages[key])}</span> : DASH}
      {row.state === 'cancelled' && <span className={cx(styles.pill, styles.cancelledTag)}>{formatMessage(messages.orpStatusCancelled)}</span>}
    </span>
  );
};

const EyeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
  </svg>
);

const COLUMNS = [
  { key: 'checkIn', label: 'orpColDate', sortable: true },
  { key: 'confirmationCode', label: 'orpColReservation', sortable: true },
  { key: 'vehicle', label: 'orpColVehicle', sortable: true },
  { key: 'types', label: 'orpColTypes' },
  { key: 'grossAmount', label: 'orpColGross', sortable: true, numeric: true },
  { key: 'netRevenue', label: 'orpColNet', sortable: true, numeric: true },
  { key: 'status', label: 'orpColStatus', sortable: true },
  { key: 'method', label: 'orpColMethod' },
  { key: 'reference', label: 'orpColReference' },
  { key: 'actions', label: 'orpColActions' },
];

// History of revenue and payouts: ONE LINE PER RESERVATION. Sorting is done by the server (onSort asks for another order).
const EarningsTable = ({ rows, sortBy, sortDir, onSort, onView, currency }) => {
  const { formatMessage, locale } = useIntl();
  const styles = { ...sh, ...s };
  return (
    <div className={s.tableWrap}>
      <table className={s.table}>
        <thead>
          <tr>
            {COLUMNS.map((column) => {
              const active = column.sortable && sortBy === column.key;
              const label = formatMessage(messages[column.label]);
              return (
                <th key={column.key} scope="col" className={cx(column.numeric && s.numeric)}
                  aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : (column.sortable ? 'none' : undefined)}>
                  {column.sortable
                    ? (
                      <button type="button" className={s.sortButton} onClick={() => onSort(column.key)}>
                        {label}
                        <span className={cx(s.sortIcon, active && s.sortIconActive)} aria-hidden="true">{active ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</span>
                      </button>
                    )
                    : (column.key === 'actions' ? <span className={sh.srOnly}>{label}</span> : label)}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const payment = row.payment || {};
            const photo = photoUrl(row.photo);
            return (
              <tr key={row.reservationId}>
                <td data-label={formatMessage(messages.orpColDate)}>{formatDay(row.checkIn, locale)}</td>
                <td data-label={formatMessage(messages.orpColReservation)} className={s.code}>#{row.confirmationCode}</td>
                <td data-label={formatMessage(messages.orpColVehicle)}>
                  <span className={s.vehicle}>
                    <span className={s.thumb} style={photo ? { backgroundImage: `url("${photo}")` } : undefined} aria-hidden="true" />
                    <span className={s.vehicleTitle}>{row.title}</span>
                  </span>
                </td>
                <td data-label={formatMessage(messages.orpColTypes)}><TypePills components={row.components} styles={styles} /></td>
                <td data-label={formatMessage(messages.orpColGross)} className={s.numeric}>{formatMoney(row.grossAmount, currency, locale)}</td>
                <td data-label={formatMessage(messages.orpColNet)} className={cx(s.numeric, s.strong)}>{formatMoney(row.netRevenue, currency, locale)}</td>
                <td data-label={formatMessage(messages.orpColStatus)}><StatusPill row={row} styles={styles} /></td>
                <td data-label={formatMessage(messages.orpColMethod)}>{payment.method || DASH}</td>
                <td data-label={formatMessage(messages.orpColReference)}>{payment.reference || DASH}</td>
                <td data-label={formatMessage(messages.orpColActions)}>
                  <button type="button" className={s.iconButton} onClick={(event) => onView(row, event.currentTarget)}
                    aria-label={`${formatMessage(messages.orpViewDetails)} #${row.confirmationCode}`} title={formatMessage(messages.orpViewDetails)}>
                    <EyeIcon />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default withStyles(sh, s)(EarningsTable);
